import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save } from "lucide-react";
import { useTasks } from "@/hooks/use-tasks";
import { useToast } from "@/hooks/use-toast";
import { ClaimTask } from "@/api/generated/client/models";
import { format } from "date-fns";

const updateTaskSchema = z.object({
    title: z.string().min(1, "Title is required"),
    description: z.string().optional(),
    priority: z.enum(["low", "medium", "high", "urgent"]),
    dueDate: z.string().optional(),
    assignedTo: z.string().optional(),
});

type UpdateTaskFormValues = z.infer<typeof updateTaskSchema>;

interface UpdateTaskModalProps {
    claimId: number;
    task: ClaimTask | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function UpdateTaskModal({ claimId, task, open, onOpenChange }: UpdateTaskModalProps) {
    const { updateTask, isUpdating, assignableUsers } = useTasks(claimId);
    const { toast } = useToast();

    const form = useForm<UpdateTaskFormValues>({
        resolver: zodResolver(updateTaskSchema) as any,
        defaultValues: {
            title: "",
            description: "",
            priority: "medium",
            dueDate: "",
            assignedTo: "",
        }
    });

    useEffect(() => {
        if (task && open) {
            form.reset({
                title: task.title,
                description: task.description || "",
                priority: task.priority as any,
                dueDate: task.dueDate ? format(new Date(task.dueDate), "yyyy-MM-dd") : "",
                assignedTo: task.assignedTo ? String(task.assignedTo) : "",
            });
        }
    }, [task, open, form]);

    const onSubmit = (data: UpdateTaskFormValues) => {
        if (!task) return;

        // Parse date string to create a Date at noon local time to avoid timezone issues
        let dueDate: Date | null = null;
        if (data.dueDate) {
            const [year, month, day] = data.dueDate.split('-').map(Number);
            dueDate = new Date(year, month - 1, day, 12, 0, 0);
        }

        updateTask({
            taskId: task.id,
            updates: {
                title: data.title,
                description: data.description,
                priority: data.priority as any,
                dueDate,
                assignedTo: data.assignedTo ? Number(data.assignedTo) : null,
            }
        }, {
            onSuccess: () => {
                toast({ title: "Success", description: "Task updated successfully" });
                onOpenChange(false);
            },
            onError: () => {
                toast({ title: "Error", description: "Failed to update task", variant: "destructive" });
            }
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Edit Task</DialogTitle>
                    <DialogDescription>
                        Update the details of this task.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit) as any} className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label htmlFor="title">Title</Label>
                        <Input
                            id="title"
                            placeholder="Task title"
                            {...form.register("title")}
                        />
                        {form.formState.errors.title && (
                            <p className="text-xs text-destructive">{form.formState.errors.title.message}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                            id="description"
                            placeholder="Task details..."
                            {...form.register("description")}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="priority">Priority</Label>
                            <Select
                                onValueChange={(val) => form.setValue("priority", val as any)}
                                value={form.watch("priority")}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select priority" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                    <SelectItem value="urgent">Urgent</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="dueDate">Due Date</Label>
                            <Input
                                id="dueDate"
                                type="date"
                                {...form.register("dueDate")}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="assignedTo">Assignee</Label>
                        <Select
                            onValueChange={(val) => form.setValue("assignedTo", val === "unassigned" ? "" : val)}
                            value={form.watch("assignedTo") || "unassigned"}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select assignee" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="unassigned">Unassigned</SelectItem>
                                {assignableUsers.map((u) => (
                                    <SelectItem key={u.id} value={String(u.id)}>
                                        {u.name || u.email}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <DialogFooter className="pt-4">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isUpdating} className="gap-2">
                            {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            Save Changes
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
